// ===================================
// KAIXO - PublishModal
// ===================================
// Modal dedicado de publicación. Reemplaza la UI de publicar que vivía
// en la vista chat. Dos modos independientes: 'latido' (permanente,
// feed) y 'story' (efímero 24h, carrusel superior).
//
// Campos visibles según el modo:
//   latido  → visibility + comments + contact + intereses + media + caption + publicar
//   story   → visibility + comments + intereses + media + publicar (sin caption)
// ===================================

import React, { useState, useRef, useEffect } from 'react';
import ReactDOM from 'react-dom';
import { Icons } from '../Icons/Icons';
import { v2PublishCarousel } from '../../services/api';
import ct from './i18n';
import { langToISO } from '../../utils/langCode';
import { INTERESTS, getInterestLabel } from '../../constants/interests';
import EmojiPicker from '../EmojiPicker/EmojiPicker';
import useModalClose from '../../hooks/useModalClose';
import './PublishModal.css';

export default function PublishModal({ channel, mode = 'latido', currentUser, onClose, onPublished, initialMedia = null }) {
    useModalClose(onClose);
    const isStory = mode === 'story';
    const [text, setText] = useState('');
    // initialMedia: si se pasa { file, type }, precargamos la lista de
    // pendingMedia con ese archivo. Útil cuando se llega aquí desde la
    // cámara con la foto ya tomada (selector de destino tras captura).
    const [pendingMedia, setPendingMedia] = useState(() => {
        if (initialMedia && initialMedia.file) {
            return [{
                file: initialMedia.file,
                previewUrl: URL.createObjectURL(initialMedia.file),
                type: initialMedia.type === 'video' ? 'video' : 'image'
            }];
        }
        return [];
    });
    const [visibility, setVisibility] = useState('subscribers');
    const [commentsEnabled, setCommentsEnabled] = useState(true);
    const [contactMode, setContactMode] = useState('');
    const [postInterests, setPostInterests] = useState([]);
    const [uploading, setUploading] = useState(false);
    const [error, setError] = useState('');
    const [showEmojiPicker, setShowEmojiPicker] = useState(false);
    const inputRef = useRef(null);

    // Toggle de un interés (multi-checkbox inline). Tope de 10.
    const toggleInterest = (key) => {
        setPostInterests(prev => {
            if (prev.includes(key)) return prev.filter(k => k !== key);
            if (prev.length >= 10) return prev;
            return [...prev, key];
        });
    };

    const isBusiness = channel?.type === 'business';

    useEffect(() => {
        return () => {
            pendingMedia.forEach(m => {
                if (m.previewUrl?.startsWith('blob:')) URL.revokeObjectURL(m.previewUrl);
            });
        };
    }, [pendingMedia]);

    const handleMediaPick = (e) => {
        const files = Array.from(e.target.files || []);
        if (!files.length) return;
        const merged = [...pendingMedia];
        for (const file of files) {
            if (merged.length >= 10) break;
            const isVideo = (file.type || '').startsWith('video/');
            merged.push({
                file,
                previewUrl: URL.createObjectURL(file),
                type: isVideo ? 'video' : 'image'
            });
        }
        setPendingMedia(merged);
        e.target.value = '';
    };

    const removeMedia = (idx) => {
        const item = pendingMedia[idx];
        if (item?.previewUrl?.startsWith('blob:')) URL.revokeObjectURL(item.previewUrl);
        setPendingMedia(pendingMedia.filter((_, i) => i !== idx));
    };

    const canPublish = () => {
        if (uploading) return false;
        if (isStory) return pendingMedia.length > 0;
        return !!(text.trim() || pendingMedia.length > 0);
    };

    const handlePublish = async () => {
        if (!canPublish()) return;

        // Validación 3-10 intereses para canales business
        if (isBusiness && (postInterests.length < 3 || postInterests.length > 10)) {
            setError(ct.interestsCountError || 'Selecciona entre 3 y 10 intereses');
            return;
        }
        if (!isBusiness && postInterests.length > 10) {
            setError(ct.interestsCountError || 'Máximo 10 intereses');
            return;
        }

        setUploading(true);
        setError('');
        try {
            const fd = new FormData();
            pendingMedia.forEach(m => fd.append('media', m.file));
            fd.append('senderId', currentUser._id);
            fd.append('caption', isStory ? '' : text.trim());
            fd.append('visibility', visibility);
            fd.append('contactMode', isStory ? 'none' : contactMode);
            fd.append('commentsEnabled', String(!!commentsEnabled));
            fd.append('interests', JSON.stringify(postInterests));
            fd.append('isStory', String(isStory));
            const { data } = await v2PublishCarousel(channel._id, fd);
            onPublished?.(data.message);
            // Evento global para que otros componentes (perfil del canal,
            // StoriesBar si es historia) se refresquen sin reload total.
            try {
                window.dispatchEvent(new CustomEvent('v2_post_published', {
                    detail: { message: data.message, isStory }
                }));
            } catch {}
            onClose?.();
        } catch (err) {
            setError(err.response?.data?.error || err.message);
        } finally {
            setUploading(false);
        }
    };

    return ReactDOM.createPortal(
        <div className="pm-overlay" onClick={(e) => e.target.classList.contains('pm-overlay') && !uploading && onClose()}>
            <div className="pm-container">
                <div className="pm-header">
                    <button className="pm-close" onClick={onClose} disabled={uploading} aria-label="Cerrar">
                        <Icons.Close size={20} />
                    </button>
                    <h3 className="pm-title">
                        {isStory ? (ct.publishStory || 'Publicar historia') : (ct.publishLatido || 'Publicar latido')}
                    </h3>
                </div>

                <div className="pm-body">

                    {/* Caption: solo en latido. Con botón emoji al lado. */}
                    {!isStory && (
                        <div style={{ position: 'relative' }}>
                            <textarea
                                className="pm-caption"
                                value={text}
                                onChange={e => setText(e.target.value)}
                                placeholder={ct.publishPlaceholder || 'Escribe tu latido…'}
                                rows={3}
                                maxLength={5000}
                                disabled={uploading}
                                spellCheck={true}
                                lang={langToISO(currentUser?.language)}
                                autoCorrect="on"
                                autoCapitalize="sentences"
                            />
                            <div className="kx-emoji-wrapper" style={{ position: 'absolute', right: 8, bottom: 8 }}>
                                <button
                                    type="button"
                                    className="kx-emoji-trigger"
                                    onClick={() => setShowEmojiPicker(v => !v)}
                                    aria-label="Insertar emoji"
                                    title="Insertar emoji"
                                    disabled={uploading}
                                >😊</button>
                                <EmojiPicker
                                    open={showEmojiPicker}
                                    onPick={(em) => {
                                        setText(prev => prev + em);
                                        setShowEmojiPicker(false);
                                    }}
                                    onClose={() => setShowEmojiPicker(false)}
                                />
                            </div>
                        </div>
                    )}

                    {/* Media picker */}
                    <div className="pm-media-wrap">
                        <input
                            ref={inputRef}
                            type="file"
                            accept="image/*,video/*"
                            multiple
                            onChange={handleMediaPick}
                            style={{ display: 'none' }}
                        />
                        <button
                            className="pm-media-add"
                            onClick={() => inputRef.current?.click()}
                            disabled={uploading || pendingMedia.length >= 10}
                        >
                            <Icons.ImageOutline size={18} />
                            <span>{ct.attachImage || 'Añadir foto o video'}</span>
                            {pendingMedia.length > 0 && (
                                <span className="pm-media-count">{pendingMedia.length}/10</span>
                            )}
                        </button>

                        {pendingMedia.length > 0 && (
                            <>
                                <div className="pm-previews">
                                    {pendingMedia.map((m, i) => (
                                        <div key={i} className="pm-preview">
                                            {m.type === 'video'
                                                ? <video src={m.previewUrl} muted playsInline />
                                                : <img src={m.previewUrl} alt="" />}
                                            <button
                                                className="pm-preview-remove"
                                                onClick={() => removeMedia(i)}
                                                disabled={uploading}
                                                aria-label="Quitar"
                                            >
                                                <Icons.Close size={12} color="white" />
                                            </button>
                                        </div>
                                    ))}
                                </div>
                                <div className="pm-hint">
                                    💡 Para que cubra la pantalla sin barras negras, usa <strong>vertical 9:16</strong> (ideal 1080×1920).
                                </div>
                            </>
                        )}
                    </div>

                    {/* Configuración común */}
                    <div className="pm-settings">

                        {/* Visibilidad */}
                        <div className="pm-field">
                            <label>{ct.visibility || 'Visibilidad'}</label>
                            <select value={visibility} onChange={e => setVisibility(e.target.value)} disabled={uploading}>
                                <option value="subscribers">{ct.visPublic || 'Público'}</option>
                                <option value="private">{ct.visPrivate || 'Privado'}</option>
                            </select>
                        </div>

                        {/* Comentarios */}
                        <div className="pm-field">
                            <label>{ct.comments || 'Comentarios'}</label>
                            <button
                                type="button"
                                className={`pm-toggle ${commentsEnabled ? 'pm-toggle-on' : ''}`}
                                onClick={() => setCommentsEnabled(!commentsEnabled)}
                                disabled={uploading}
                            >
                                {commentsEnabled ? (ct.commentsOn || 'Sí') : (ct.commentsOff || 'No')}
                            </button>
                        </div>

                        {/* Contacto: solo en latido */}
                        {!isStory && isBusiness && (
                            <div className="pm-field">
                                <label>{ct.contactModeShort || 'Contacto'}</label>
                                <select value={contactMode} onChange={e => setContactMode(e.target.value)} disabled={uploading}>
                                    <option value="">{ct.contactModeInherit || 'Heredar del canal'}</option>
                                    <option value="none">{ct.contactModeNone || 'Ninguno'}</option>
                                    <option value="direct">{ct.contactModeDirect || 'Mensaje directo'}</option>
                                    <option value="form">{ct.contactModeForm || 'Formulario'}</option>
                                    <option value="both">{ct.contactModeBoth || 'Ambos'}</option>
                                </select>
                            </div>
                        )}

                        {/* Intereses — multi-checkbox inline (max 10) */}
                        <div className="pm-field pm-field-full">
                            <label style={{ display:'flex', justifyContent:'space-between', alignItems:'center' }}>
                                <span>
                                    {ct.interests || 'Intereses'}
                                    {isBusiness
                                        ? <span className="pm-required"> (3-10 requeridos)</span>
                                        : <span style={{ color:'#888', fontWeight:400, fontSize:12 }}> (máximo 10)</span>}
                                </span>
                                <span style={{ fontSize:12, color:'#888', fontWeight:600 }}>
                                    {postInterests.length}/10
                                </span>
                            </label>
                            <div className="pm-interests-grid" style={{ display:'grid', gridTemplateColumns:'repeat(auto-fill, minmax(140px, 1fr))', gap:6, marginTop:6 }}>
                                {INTERESTS.map(key => {
                                    const checked = postInterests.includes(key);
                                    const disabled = uploading || (!checked && postInterests.length >= 10);
                                    return (
                                        <label
                                            key={key}
                                            style={{
                                                display:'flex',
                                                alignItems:'center',
                                                gap:8,
                                                padding:'8px 10px',
                                                borderRadius:8,
                                                border: checked ? '1.5px solid #ff6b35' : '1.5px solid #e0e0e0',
                                                background: checked ? 'rgba(255,107,53,0.08)' : '#fff',
                                                cursor: disabled ? 'not-allowed' : 'pointer',
                                                opacity: disabled && !checked ? 0.5 : 1,
                                                fontSize:13,
                                                userSelect:'none',
                                                transition:'all .15s'
                                            }}
                                        >
                                            <input
                                                type="checkbox"
                                                checked={checked}
                                                disabled={disabled}
                                                onChange={() => toggleInterest(key)}
                                                style={{ accentColor:'#ff6b35', flexShrink:0 }}
                                            />
                                            <span style={{ flex:1, lineHeight:1.2 }}>
                                                {getInterestLabel(key, currentUser?.language || 'español')}
                                            </span>
                                        </label>
                                    );
                                })}
                            </div>
                        </div>
                    </div>

                    {error && <div className="pm-error">{error}</div>}

                    <button
                        className={`pm-publish-btn ${isStory ? 'pm-publish-story' : ''}`}
                        onClick={handlePublish}
                        disabled={!canPublish()}
                    >
                        {uploading ? (ct.loading || 'Publicando…') : (
                            isStory
                                ? (ct.publishStory || 'Publicar historia')
                                : (ct.publishLatido || 'Publicar latido')
                        )}
                    </button>
                </div>
            </div>
        </div>,
        document.body
    );
}
